import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";

import { SUPPORT_EMAIL } from "../../constants";
import { theme } from "../../constants/theme";

const SuggestionsDropdown = styled.div`
  border: 1px solid ${theme.color.border};
  background: ${theme.color.surface};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.lg};
  overflow: hidden;

  .react-autosuggest__suggestions-list {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
  }
  .react-autosuggest__suggestion {
    padding: 0.75rem 1.25rem;
    color: ${theme.color.text};
    font-size: 0.95rem;
    border-bottom: 1px solid ${theme.color.border};
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  .react-autosuggest__suggestions-list li:last-child .react-autosuggest__suggestion {
    border-bottom: none;
  }
  .react-autosuggest__suggestion--highlighted {
    background-color: rgba(245, 168, 138, 0.14);
    color: ${theme.color.slate};
  }

  .address-escape-hatch {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    border: none;
    border-top: 1px solid ${theme.color.border};
    background: rgba(245, 168, 138, 0.06);
    padding: 0.7rem 1.25rem;
    font-size: 0.88rem;
    color: ${theme.color.textMuted};
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .address-escape-hatch:hover {
    background: rgba(245, 168, 138, 0.14);
  }
  .address-escape-hatch i {
    color: ${theme.color.salmon};
    font-size: 0.82rem;
  }
  .address-escape-hatch strong {
    color: ${theme.color.salmonDeep};
    font-weight: ${theme.weight.semibold};
    white-space: nowrap;
  }
`;

const AddressNotFound = styled.div`
  color: ${theme.color.error};
  margin-top: 0.65rem;
  font-size: 0.9rem;
`;

const RetryButton = styled.button`
  appearance: none;
  border: 0;
  background: none;
  color: ${theme.color.slate};
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
`;

export const AddressNotFoundMessage = () => (
  <AddressNotFound>
    We couldn't find that address. Please enter the full street address, or
    contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for help.
  </AddressNotFound>
);

export const AddressSearchErrorMessage = ({ onRetry }) => (
  <AddressNotFound role="alert">
    We couldn’t reach address search just now. Please check your connection
    and <RetryButton type="button" onClick={onRetry}>try again</RetryButton>.
  </AddressNotFound>
);

AddressSearchErrorMessage.propTypes = {
  onRetry: PropTypes.func.isRequired,
};

export const AddressSuggestionsPortal = ({
  children,
  containerProps,
  getDropdownStyle,
  onSearchEveryState,
  showEscapeHatch,
}) => {
  const dropdown = (
    <SuggestionsDropdown {...containerProps} style={getDropdownStyle()}>
      {children}
      {showEscapeHatch && (
        <button
          type="button"
          className="address-escape-hatch"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onSearchEveryState}
        >
          <i className="fa fa-search" aria-hidden="true" />
          <span>
            Don't see your address? <strong>Search every state →</strong>
          </span>
        </button>
      )}
    </SuggestionsDropdown>
  );

  return typeof document !== "undefined"
    ? ReactDOM.createPortal(dropdown, document.body)
    : dropdown;
};

AddressSuggestionsPortal.propTypes = {
  children: PropTypes.node,
  containerProps: PropTypes.object,
  getDropdownStyle: PropTypes.func.isRequired,
  onSearchEveryState: PropTypes.func.isRequired,
  showEscapeHatch: PropTypes.bool.isRequired,
};

AddressSuggestionsPortal.defaultProps = {
  children: null,
  containerProps: {},
};
